<template>
  <div class="order-analysis-page">
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title"><i class="el-icon-s-order"></i> 订单分析</h2>
        <span class="page-desc">分析订单量趋势、状态分布及订单完成情况</span>
      </div>
    </div>
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="loadData">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 概览卡片 -->
    <div class="section-title"><i class="el-icon-tickets"></i> 订单概览</div>
    <div class="overview-cards">
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#409EFF,#66B1FF)"><i class="el-icon-document-add"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ formatNumber(stats.totalOrders) }}</div><div class="ov-card-label">订单总数</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#67C23A,#85CE61)"><i class="el-icon-circle-check"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ formatNumber(stats.completedOrders) }}</div><div class="ov-card-label">已完成</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#E6A23C,#EBB563)"><i class="el-icon-time"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ formatNumber(stats.pendingOrders) }}</div><div class="ov-card-label">待出行</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#F56C6C,#F78989)"><i class="el-icon-circle-close"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ formatNumber(stats.cancelledOrders) }}</div><div class="ov-card-label">已取消</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#909399,#A6A9AD)"><i class="el-icon-money"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.totalAmount) }}</div><div class="ov-card-label">订单金额</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#9B59B6,#B07CC6)"><i class="el-icon-user"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ formatNumber(stats.totalPassengers) }}</div><div class="ov-card-label">乘客人次</div></div>
      </div>
    </div>

    <!-- 图表 3列 -->
    <div class="charts-grid">
      <div class="chart-card" style="grid-column:span 2">
        <div class="chart-card-header">
          <span><i class="el-icon-data-line"></i> 订单趋势分析</span>
          <el-radio-group v-model="trendType" size="mini">
            <el-radio-button label="order">订单量</el-radio-button>
            <el-radio-button label="amount">订单金额</el-radio-button>
            <el-radio-button label="passenger">乘客人次</el-radio-button>
          </el-radio-group>
        </div>
        <div class="chart-body" ref="orderTrendChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-pie-chart"></i> 订单状态分布</span></div>
        <div class="chart-body" ref="orderStatusChart"></div>
      </div>
    </div>

    <!-- 图表 2列 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-s-marketing"></i> 热门线路排行</span></div>
        <div class="chart-body" ref="lineRankChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-time"></i> 订单时段分布</span></div>
        <div class="chart-body" ref="hourDistChart"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { orderAnalysisMock } from "@/assets/js/dataAnalysisMock.js";

export default {
  name: "OrderAnalysis",
  data() {
    return {
      loading: false,
      timeType: 0,
      trendType: "order",
      stats: {
        totalOrders: 0,
        completedOrders: 0,
        pendingOrders: 0,
        cancelledOrders: 0,
        totalAmount: 0,
        totalPassengers: 0
      },
      trendData: [],
      charts: {},
      resizeHandler: null
    };
  },
  watch: {
    trendType() {
      this.renderTrendChart();
    }
  },
  mounted() {
    this.loadData();
    this.resizeHandler = () => Object.values(this.charts).forEach(c => c && c.resize());
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    Object.values(this.charts).forEach(chart => chart && chart.dispose());
  },
  methods: {
    formatNumber(num) {
      if (!num) return "0";
      return num.toLocaleString();
    },
    formatMoney(num) {
      if (!num) return "0.00";
      return parseFloat(num).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    initChart(refName) {
      if (this.charts[refName]) this.charts[refName].dispose();
      const dom = this.$refs[refName];
      if (!dom) return null;
      const chart = echarts.init(dom);
      this.charts[refName] = chart;
      return chart;
    },
    async loadData() {
      this.loading = true;
      await Promise.all([
        this.loadStats(),
        this.loadTrendData(),
        this.loadStatusData(),
        this.loadSourceData(),
        this.loadLineRank(),
        this.loadHourDist()
      ]);
      this.loading = false;
    },
    async loadStats() {
      try {
        // 使用模拟数据
        const res = orderAnalysisMock.getStats();
        if (res && res.code === 1) {
          const data = res.data || {};
          this.stats = {
            totalOrders: data.orderCreateNum || 0,
            completedOrders: data.orderFinishNum || 0,
            pendingOrders: data.orderPayNum || 0,
            cancelledOrders: data.orderRefundNum || 0,
            totalAmount: data.orderPayAmount || 0,
            totalPassengers: data.passengerNum || 0
          };
        }
      } catch (e) {
        console.error("加载统计数据失败", e);
      }
    },
    async loadTrendData() {
      try {
        // 使用模拟数据
        const res = orderAnalysisMock.getTrendData();
        if (res && res.code === 1) {
          this.trendData = res.data?.statisticsReportVos || [];
        }
      } catch (e) {
        console.error("加载趋势数据失败", e);
        this.trendData = [];
      }
      this.renderTrendChart();
    },
    renderTrendChart() {
      const xData = this.trendData.map(item => {
        let time = item.time || "";
        if (time.includes(" ")) time = time.split(" ")[0];
        if (time.length === 10) time = time.substring(5);
        return time;
      });

      let seriesData, seriesName, color;
      if (this.trendType === "order") {
        seriesData = this.trendData.map(item => item.orderCreateNum || 0);
        seriesName = "订单量";
        color = "#409EFF";
      } else if (this.trendType === "amount") {
        seriesData = this.trendData.map(item => item.orderPayAmount || 0);
        seriesName = "订单金额";
        color = "#67C23A";
      } else {
        seriesData = this.trendData.map(item => item.passengerNum || 0);
        seriesName = "乘客人次";
        color = "#E6A23C";
      }

      const option = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(50, 50, 50, 0.9)",
          textStyle: { color: "#fff" },
          formatter: params => {
            const p = params[0];
            let value = p.value;
            if (this.trendType === "amount") value = "¥" + value.toLocaleString();
            return `${p.name}<br/>${p.seriesName}: ${value}`;
          }
        },
        grid: { left: "3%", right: "4%", bottom: "10%", top: "10%", containLabel: true },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: xData,
          axisLine: { lineStyle: { color: "#ddd" } },
          axisLabel: { color: "#666" }
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#f0f0f0" } },
          axisLabel: { color: "#666" }
        },
        series: [{
          name: seriesName,
          type: "line",
          smooth: true,
          data: seriesData,
          itemStyle: { color },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: color.replace(")", ", 0.3)").replace("rgb", "rgba") },
              { offset: 1, color: color.replace(")", ", 0.05)").replace("rgb", "rgba") }
            ])
          }
        }]
      };
      const c = this.initChart('orderTrendChart'); c && c.setOption(option);
    },
    loadStatusData() {
      const option = {
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { orient: "vertical", right: "5%", top: "center", textStyle: { color: "#666" } },
        series: [{
          type: "pie",
          radius: ["40%", "65%"],
          center: ["35%", "50%"],
          label: { show: false },
          emphasis: { label: { show: true, fontSize: 14, fontWeight: "bold" } },
          data: [
            { value: this.stats.completedOrders, name: "已完成", itemStyle: { color: "#67C23A" } },
            { value: this.stats.pendingOrders, name: "待出行", itemStyle: { color: "#409EFF" } },
            { value: this.stats.cancelledOrders, name: "已取消", itemStyle: { color: "#F56C6C" } }
          ]
        }]
      };
      const c2 = this.initChart('orderStatusChart'); c2 && c2.setOption(option);
    },
    loadSourceData() {
    },
    loadLineRank() {
      // 使用模拟数据
      const res = orderAnalysisMock.getLineRank();
      const data = res?.data || [];
      const option = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "10%", bottom: "3%", top: "3%", containLabel: true },
        xAxis: {
          type: "value",
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#f0f0f0" } },
          axisLabel: { color: "#666" }
        },
        yAxis: {
          type: "category",
          data: data.map(item => item.lineName),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: "#666" }
        },
        series: [{
          type: "bar",
          data: data.map(item => item.orderNum),
          barWidth: 16,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: "#409EFF" },
              { offset: 1, color: "#66B1FF" }
            ]),
            borderRadius: [0, 8, 8, 0]
          },
          label: { show: true, position: "right", color: "#666" }
        }]
      };
      const c3 = this.initChart('lineRankChart'); c3 && c3.setOption(option);
    },
    loadHourDist() {
      // 使用模拟数据
      const res = orderAnalysisMock.getHourDist();
      const data = res?.data || [];
      const option = {
        tooltip: { trigger: "axis" },
        grid: { left: "3%", right: "4%", bottom: "10%", top: "10%", containLabel: true },
        xAxis: {
          type: "category",
          data: data.map(item => item.hour),
          axisLine: { lineStyle: { color: "#ddd" } },
          axisLabel: { color: "#666", interval: 2 }
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#f0f0f0" } },
          axisLabel: { color: "#666" }
        },
        series: [{
          type: "bar",
          data: data.map(item => item.orderNum),
          barWidth: 12,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#409EFF" },
              { offset: 1, color: "#66B1FF" }
            ]),
            borderRadius: [4, 4, 0, 0]
          }
        }]
      };
      const c4 = this.initChart('hourDistChart'); c4 && c4.setOption(option);
    }
  }
};
</script>

<style lang="less" scoped>
.order-analysis-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-bar {
    display: flex; align-items: center; justify-content: space-between;
    background: #fff; padding: 16px 20px; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 20px;
  }

  .section-title {
    font-size: 16px; font-weight: 600; color: #303133; margin-bottom: 16px;
    display: flex; align-items: center;
    i { margin-right: 8px; color: #409EFF; }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    .ov-card {
      background: #fff; border-radius: 10px; padding: 16px;
      display: flex; align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.2s ease;
      &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
      .ov-card-icon {
        width: 40px; height: 40px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        margin-right: 12px; flex-shrink: 0;
        i { color: #fff; font-size: 18px; }
      }
      .ov-card-info {
        min-width: 0;
        .ov-card-value { font-size: 18px; font-weight: 700; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ov-card-label { font-size: 12px; color: #909399; margin-top: 2px; }
      }
    }
  }

  .chart-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; min-width: 0;
    .chart-card-header {
      padding: 14px 20px; border-bottom: 1px solid #f0f0f0;
      font-size: 14px; font-weight: 600; color: #303133;
      display: flex; align-items: center; justify-content: space-between;
      i { margin-right: 6px; color: #409EFF; }
    }
    .chart-body { width: 100%; height: 320px; padding: 10px; box-sizing: border-box; }
  }

  .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
  .charts-grid-half { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 24px; }

  @media (max-width: 1400px) {
    .overview-cards { grid-template-columns: repeat(3, 1fr); }
    .charts-grid { grid-template-columns: repeat(2, 1fr); }
  }
}
</style>
